/*************************************************
 * ByzFunder
 * smartlook.ts
 * Smartlook analytics service for React + TypeScript
 *************************************************/

const SMARTLOOK_KEY =  process.env.REACT_APP_SMARTLOOK_KEY ?? "db128f3e33cc211a4f08fa64390f2875c37296bc";

declare global {
  interface SmartlookFunction {
    (...args: any[]): void;
    api?: any[];
  }

  interface Window {
    smartlook: SmartlookFunction;
    _smartlookInitialized?: boolean;
  }
}

let isInitialized = false;

/**
 * Initialize Smartlook
 * Call after user login
 */
export const initSmartlook = (user?: any): void => {
  if (typeof window === "undefined") return;

  // prevent localhost tracking
  if (window.location.hostname === "localhost") return;
  console.log("Initializing Smartlook. Already initialized:", isInitialized);

  try {
    // If SDK script has already been injected before (either fully loaded or still loading),
    // just (re)start recording and identify the user. This covers:
    // - Subsequent logins in the same tab after logout
    // - Cases where init is called multiple times while the script is still loading
    if (window._smartlookInitialized) {
      try {
        if (typeof window.smartlook === "function") {
          // For a fully loaded SDK, this resumes recording after a pause.
          // For the stub, this just queues the call until SDK attaches.
          window.smartlook("record");
        }
        if (user) {
          identifyUser(user);
        }
        isInitialized = true;
      } catch (resumeError) {
        console.warn("Smartlook resume/init failed:", resumeError);
      }
      return;
    }

    // First-time init: create stub and load script
    // Smartlook env key missing – do not attempt to load
    if (!SMARTLOOK_KEY) {
      console.warn("Smartlook init skipped: REACT_APP_SMARTLOOK_KEY is not set");
      return;
    }

    window.smartlook = function () {
      (window.smartlook.api = window.smartlook.api || []).push(arguments);
    };

    window._smartlookInitialized = true;

    // Load Smartlook script
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://web-sdk.smartlook.com/recorder.js";

    script.onload = () => {
      console.log("Smartlook script loaded");

      window.smartlook("init", SMARTLOOK_KEY);

      try {
        window.smartlook("record", {
          ips: true,
          forms: true,
          emails: true,
          numbers: true,
        });
      } catch (recordError) {
        console.warn("Smartlook record call failed on init:", recordError);
      }

      if (user) {
        identifyUser(user);
      }
      isInitialized = true;
      console.log("Smartlook initialized");
    };

    script.onerror = (error) => {
      console.warn("Smartlook script failed to load:", error);
      // Allow future re-init attempts if load fails
      window._smartlookInitialized = false;
    };

    document.head.appendChild(script);
  } catch (error) {
    console.warn("Smartlook init failed:", error);
  }
};

/**
 * Identify logged-in user
 */
export const identifyUser = (user?: any): void => {
  if (!user || !window.smartlook) return;

  try {
    const visitorId =
      user?.id ??
      user?.user_id ??
      user?.userId ??
      user?.uuid ??
      user?.email ??
      "unknown";

    window.smartlook("identify", String(visitorId), user);

    console.log("Smartlook user identified:", visitorId);
  } catch (error) {
    console.warn("Smartlook identify failed:", error);
  }
};

/**
 * Destroy Smartlook session
 * Call on logout
 */
export const destroySmartlook = (): void => {
  if (!isInitialized || !window.smartlook) return;

  try {
    window.smartlook("anonymize");

    isInitialized = false;

    console.log("Smartlook destroyed");
  } catch (error) {
    console.warn("Smartlook destroy failed:", error);
  }
};

/**
 * Track navigation (React Router)
 */
export const trackNavigateStepPath = (path: string): void => {
  if (!isInitialized || !window.smartlook) return;

  try {
    window.smartlook("navigation", path);
  } catch {
    // ignore errors
  }
};
